import { useState } from 'react'
import { Accordion, Button, ButtonLink, EmptyState, Icon, ImagePlaceholder, Input, OptionGrid, Skeleton, filterAccordionItems } from '@/ds'
import type { AccordionItem } from '@/ds'
import { useCopy } from '@/ds/utils/useCopy'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { telHref } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { hasMarkup, richText } from '@/lib/richText'
import { plainText } from '@/lib/text'
import { useAsync } from '@/lib/useAsync'
import { guiaService } from '@/services/guia'
import type { Guide, GuideSection, NearbyCategory } from '@/services/guia'
import { placeName } from '../Accesos/shared'
import { usePhotos } from '../Accesos/usePhotos'
import { SectionError } from '../Estancia/sections/SectionError'
import { useLang } from '../Reserva/shared'
import '../Pagos/Pagos.css'
import './Guia.css'

const CATEGORIES: NearbyCategory[] = ['eat', 'shop', 'see', 'kids']

/**
 * 8.7 · Guía de la casa (PantallaGuia.dc.html, Bloque 6 §6d). Buscador que filtra el acordeón y abre el primer
 * resultado, Wi-Fi con copia en un toque, teléfonos de interés y "Cerca de ti". Contenido de E1 (manual y normas);
 * disponible sin conexión con la copia guardada (D18); el HTML del PMS con lista blanca (D49).
 */
export function Guia() {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [data, reload] = useAsync(`guia:${stay.guestCode}`, (signal) => guiaService.getGuide(stay, signal))
  return (
    <main className="page page--wide">
      <div className="pagos__head">
        <h1>{t('v2.guia.titulo')}</h1>
        <p className="page__lead">{t('v2.guia.lead', { name: placeName(stay) })}</p>
      </div>
      {data.kind === 'loading' && (
        <div className="pagos__stack" aria-busy="true">
          <Skeleton height={52} />
          <Skeleton height={140} />
          <Skeleton height={56 * 5} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {data.kind === 'ready' && <GuideContent guide={data.value} />}
    </main>
  )
}

function GuideContent({ guide }: { guide: Guide }) {
  const { t } = useLang()
  const stay = useLoadedStay()
  const [query, setQuery] = useState('')
  const photos = usePhotos(guide.sections.flatMap((s) => s.images))
  const items: AccordionItem[] = guide.sections.map((s) => sectionItem(s, photos, t))
  const visible = filterAccordionItems(items, query)
  const whatsapp = stay.contact.whatsapp

  return (
    <>
      {guide.sections.length > 0 && (
        <Input
          type="search"
          label={t('v2.guia.buscar')}
          placeholder={t('v2.guia.buscarPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          leading={<Icon name="search" size={20} className="guia__search-icon" />}
          autoComplete="off"
        />
      )}
      {guide.wifi && <WifiBlock ssid={guide.wifi.ssid} password={guide.wifi.password} />}

      {guide.sections.length === 0 ? (
        <EmptyState icon="book" title={t('v2.guia.vacioTitulo')} headingLevel={2} {...(whatsapp ? { actionLabel: t('v2.ds.contact.whatsapp'), onAction: () => window.open(whatsappHref(whatsapp, stay.reference), '_blank', 'noreferrer') } : {})}>
          {t('v2.guia.vacioTexto')}
        </EmptyState>
      ) : visible.length === 0 ? (
        <EmptyState icon="search" title={t('v2.guia.sinResultados', { query: query.trim() })} headingLevel={2}>
          {t('v2.guia.sinResultadosTexto')}
        </EmptyState>
      ) : (
        <Accordion items={items} query={query} defaultOpenId={items[0]?.id ?? null} headingLevel={2} className="guia__accordion" />
      )}

      {guide.phones.length > 0 && (
        <section className="pagos__group" aria-labelledby="guia-telefonos">
          <h2 id="guia-telefonos" className="pagos__h2">
            {t('v2.legacy.preview.labels.phoneView.title')}
          </h2>
          {guide.phones.map((p) => (
            <LinkRow key={`${p.name}-${p.number}`} icon="phone" title={p.name} subtitle={p.number} href={telHref(p.number)} tabular />
          ))}
        </section>
      )}

      {guide.nearby && guide.nearby.length > 0 && <Nearby places={guide.nearby} />}
    </>
  )
}

function sectionItem(s: GuideSection, photos: Record<string, string | null>, t: ReturnType<typeof useLang>['t']): AccordionItem {
  const text = plainText(s.content)
  const images = s.images.flatMap((url) => (photos[url] ? [photos[url]] : []))
  // Texto sin marcado, fotos ni vídeos: cadena, y el acordeón resalta la búsqueda.
  if (!hasMarkup(s.content) && images.length === 0 && s.videos.length === 0) return { id: s.id, title: s.title, content: text }
  return {
    id: s.id,
    title: s.title,
    searchText: text,
    content: (
      <>
        {hasMarkup(s.content) ? <div className="guia__rich">{richText(s.content)}</div> : text && <p className="guia__text">{text}</p>}
        {(images.length > 0 || s.videos.length > 0) && (
          <div className="guia__media">
            {images.map((src, i) => (
              <img key={src} src={src} alt={t('v2.guia.foto', { name: `${s.title} ${i + 1}` })} loading="lazy" />
            ))}
            {/* El PMS no da subtítulos: el vídeo se abre aparte en lugar de incrustarlo sin ellos (docs/backend/guia.md). */}
            {s.videos.map((src, i) => (
              <LinkRow key={src} icon="image" title={t('v2.guia.video', { name: s.videos.length > 1 ? `${s.title} ${i + 1}` : s.title })} href={src} />
            ))}
          </div>
        )}
      </>
    ),
  }
}

function WifiBlock({ ssid, password }: { ssid: string; password: string | null }) {
  const { t } = useLang()
  const stay = useLoadedStay()
  const { copied, copy } = useCopy()
  const { whatsapp, supportPhone } = stay.contact
  return (
    <section className="guia__wifi" aria-labelledby="guia-wifi">
      <span className="guia__wifi-label">{t('v2.estancia.wifi.titulo')}</span>
      <h2 id="guia-wifi" className="guia__wifi-ssid">
        {ssid}
      </h2>
      {password ? (
        <>
          <span className="guia__wifi-pass">{t('v2.guia.clave', { password })}</span>
          <Button variant="secondary" icon={copied ? 'check' : 'copy'} fullWidth={false} onClick={() => void copy(password)}>
            <span role="status">{copied ? t('v2.guia.claveCopiada') : t('v2.guia.copiarClave')}</span>
          </Button>
        </>
      ) : (
        <>
          {/* Sin clave en la reserva: no se inventa ni se dice que la red es abierta; se pide a la propiedad. */}
          <span className="guia__wifi-pass">{t('v2.guia.pideClave')}</span>
          {(whatsapp || supportPhone) && (
            <div className="guia__wifi-contact">
              {whatsapp && (
                <ButtonLink variant="secondary" icon="message-circle" fullWidth={false} href={whatsappHref(whatsapp, stay.reference)} target="_blank" rel="noreferrer">
                  {t('v2.ds.contact.whatsapp')}
                </ButtonLink>
              )}
              {supportPhone && (
                <ButtonLink variant="secondary" icon="phone" fullWidth={false} href={telHref(supportPhone)}>
                  {t('v2.ds.contact.llamar')}
                </ButtonLink>
              )}
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Nearby({ places }: { places: NonNullable<Guide['nearby']> }) {
  const { t } = useLang()
  const available = CATEGORIES.filter((c) => places.some((p) => p.category === c))
  const [category, setCategory] = useState<NearbyCategory>(available[0] ?? 'eat')
  const shown = places.filter((p) => p.category === category)
  return (
    <section className="pagos__group" aria-labelledby="guia-cerca">
      <h2 id="guia-cerca" className="pagos__h2">
        {t('v2.guia.cerca')}
      </h2>
      <OptionGrid label={t('v2.guia.filtrar')} size="chip" options={CATEGORIES.map((c) => ({ id: c, label: t(`v2.guia.categoria.${c}`) }))} value={category} onChange={(id) => setCategory(id as NearbyCategory)} />
      {shown.length === 0 ? (
        <p className="pagos__note">{t('v2.guia.cercaVacio')}</p>
      ) : (
        <ul className="guia__places">
          {shown.map((p) => {
            const inner = (
              <>
                {p.photoUrl ? <img src={p.photoUrl} alt={t('v2.guia.fotoLocal')} className="guia__place-thumb" /> : <ImagePlaceholder label={t('v2.guia.fotoLocal')} ratio="1 / 1" className="guia__place-thumb" />}
                <span className="guia__place-text">
                  <h3 className="guia__place-name">{p.name}</h3>
                  <span className="guia__place-note">{p.note}</span>
                </span>
                {p.mapsUrl && <Icon name="chevron-right" size={22} className="guia__place-chevron" />}
              </>
            )
            return (
              <li key={p.id}>
                {p.mapsUrl ? (
                  <a href={p.mapsUrl} target="_blank" rel="noreferrer" className="guia__place" aria-label={t('v2.guia.verMapa', { name: p.name })}>
                    {inner}
                  </a>
                ) : (
                  <div className="guia__place">{inner}</div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
